import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/audit";

const DISMISS_KEY = "ss2026_guest_unlock_dismissed_at";
const INTERACTION_KEY = "ss2026_guest_interactions";
const DEFAULT_DELAY_MS = 90_000;
const INTERACTION_TRIGGER = 8;
const COOLDOWN_MS = 1000 * 60 * 30;

function isRegistered() {
  if (typeof window === "undefined") return true;
  try { return window.localStorage.getItem("ss2026_registered") === "true"; } catch { return false; }
}
function isGuest() {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem("ss2026_guest_mode") === "true"; } catch { return false; }
}
function lastDismissedAt(): number {
  try { return Number(window.localStorage.getItem(DISMISS_KEY) ?? 0) || 0; } catch { return 0; }
}
function readInteractions(): number {
  try { return Number(window.localStorage.getItem(INTERACTION_KEY) ?? 0) || 0; } catch { return 0; }
}

/**
 * F2 — Guest Experience → Unlock layer.
 * Fires a single boolean when an unauthenticated guest has either
 * spent enough time in the app OR hit the interaction threshold.
 */
export function useGuestUnlockTrigger(delayMs = DEFAULT_DELAY_MS) {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isRegistered() || !isGuest()) return;
    if (Date.now() - lastDismissedAt() < COOLDOWN_MS) return;

    let alive = true;
    const open = (reason: "delay" | "interactions") => {
      if (!alive) return;
      setShouldShow(true);
      void trackEvent("guest_unlock_modal_shown", { reason });
    };

    const timer = window.setTimeout(() => open("delay"), delayMs);

    const onInteraction = () => {
      const n = readInteractions() + 1;
      try { window.localStorage.setItem(INTERACTION_KEY, String(n)); } catch { /* noop */ }
      if (n >= INTERACTION_TRIGGER) open("interactions");
    };
    window.addEventListener("ss2026:guest-interaction", onInteraction);

    return () => {
      alive = false;
      window.clearTimeout(timer);
      window.removeEventListener("ss2026:guest-interaction", onInteraction);
    };
  }, [delayMs]);

  const dismiss = () => {
    setShouldShow(false);
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    void trackEvent("guest_unlock_modal_dismissed", {});
  };

  return { shouldShow, dismiss };
}

/** Helper: mark a premium-feature attempt so the trigger escalates. */
export function markGuestPremiumAttempt(featureId: string) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent("ss2026:guest-interaction", { detail: { featureId } }));
  } catch { /* noop */ }
}